import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Smartphone, Save, MessageSquare, Bell, Send, Link2, Users, ExternalLink, Trash2, Filter, Download, Image, AlertTriangle, CreditCard, Tag, Cake } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Chips de variáveis disponíveis — clicar copia a variável
function VarChips({ vars }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1 mb-1">
      {vars.map((v) => (
        <button key={v} type="button"
          onClick={() => { navigator.clipboard?.writeText(v); toast.success(`${v} copiado`); }}
          className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
          {v}
        </button>
      ))}
    </div>
  );
}

export default function AppConfigPage() {
  const { user, api, checkAuth } = useAuthStore();
  const [supportWhatsapp, setSupportWhatsapp] = useState(user?.support_whatsapp || '');
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [sending, setSending] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', button1_text: '', button1_url: '', button2_text: '', button2_url: '', expires_in_hours: '' });
  // Cupons
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: '', discount_percent: '', target_type: 'all', target_ids: [], max_uses: '' });
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [tempTargetIds, setTempTargetIds] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [showAppUsers, setShowAppUsers] = useState(false);
  const [loadingAppUsers, setLoadingAppUsers] = useState(false);
  // Aviso fixo
  const [notice, setNotice] = useState('');
  const [savingNotice, setSavingNotice] = useState(false);
  // Banners
  const [banners, setBanners] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // Birthday Gift
  const [bdayEnabled, setBdayEnabled] = useState(false);
  const [bdayType, setBdayType] = useState('coupon');
  const [bdayCouponDiscount, setBdayCouponDiscount] = useState(20);
  const [bdayRenewalMonths, setBdayRenewalMonths] = useState(1);
  const [bdaySigmaPlanId, setBdaySigmaPlanId] = useState('');
  const [savingBday, setSavingBday] = useState(false);
  // Notification job progress
  const [sendJob, setSendJob] = useState(null); // { job_id, total, sent, pushed, status }
  const jobPollRef = useRef(null);

  const fetchAppUsers = async () => {
    setLoadingAppUsers(true);
    try { const res = await api.get('/customers/with-app'); setAppUsers(res.data.customers || []); }
    catch { toast.error('Erro ao buscar'); }
    finally { setLoadingAppUsers(false); }
  };

  useEffect(() => {
    if (user?.support_whatsapp) setSupportWhatsapp(user.support_whatsapp);
    loadNotifications(); loadPlans(); loadNotice(); loadBanners(); loadCoupons(); loadCustomers(); loadBirthdaySettings(); checkActiveJob();
    return () => { if (jobPollRef.current) clearInterval(jobPollRef.current); };
  }, [user]);

  const checkActiveJob = async () => {
    try {
      const r = await api.get('/custom-notifications/active-job');
      if (r.data && r.data.status === 'running') {
        setSendJob(r.data);
        startJobPolling(r.data.id);
      }
    } catch {}
  };

  const startJobPolling = (jobId) => {
    if (jobPollRef.current) clearInterval(jobPollRef.current);
    jobPollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/custom-notifications/job/${jobId}`);
        setSendJob(r.data);
        if (r.data.status !== 'running') {
          clearInterval(jobPollRef.current);
          jobPollRef.current = null;
          if (r.data.status === 'completed') {
            toast.success(`Enviada para ${r.data.sent} clientes! (${r.data.pushed} push)`);
          }
          loadNotifications();
        }
      } catch { clearInterval(jobPollRef.current); jobPollRef.current = null; }
    }, 1500);
  };

  const loadPlans = async () => { try { const r = await api.get('/customer-plans'); setPlans(r.data); } catch {} };
  const loadNotifications = async () => { setLoadingNotifs(true); try { const r = await api.get('/custom-notifications'); setNotifications(r.data); } catch {} finally { setLoadingNotifs(false); } };
  const loadNotice = async () => { try { const r = await api.get('/app-notice'); setNotice(r.data.message || ''); } catch {} };
  const loadBanners = async () => { try { const r = await api.get('/app-banners'); setBanners(r.data || []); } catch {} };
  const loadCoupons = async () => { try { const r = await api.get('/coupons'); setCoupons(r.data || []); } catch {} };
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [buttonCount, setButtonCount] = useState(0);

  const loadCustomers = async () => { try { const r = await api.get('/customers?limit=500'); setCustomers(r.data.customers || r.data || []); } catch {} };
  const loadBirthdaySettings = async () => {
    try {
      const r = await api.get('/customer-settings');
      if (r.data) {
        setBdayEnabled(r.data.birthday_gift_enabled || false);
        setBdayType(r.data.birthday_gift_type || 'coupon');
        setBdayCouponDiscount(r.data.birthday_coupon_discount || 20);
        setBdayRenewalMonths(r.data.birthday_renewal_months || 1);
        setBdaySigmaPlanId(r.data.birthday_sigma_plan_id || '');
        // Push de vencimento
        setPushConfig({
          push_enabled_7_days: r.data.push_enabled_7_days !== false,
          push_enabled_5_days: r.data.push_enabled_5_days !== false,
          push_enabled_4_days: r.data.push_enabled_4_days !== false,
          push_enabled_3_days: r.data.push_enabled_3_days !== false,
          push_enabled_2_days: r.data.push_enabled_2_days !== false,
          push_enabled_1_day: r.data.push_enabled_1_day !== false,
          push_enabled_on_day: r.data.push_enabled_on_day !== false,
          push_enabled_1_day_after: r.data.push_enabled_1_day_after !== false,
          push_title_7_days: r.data.push_title_7_days || '',
          push_title_5_days: r.data.push_title_5_days || '',
          push_title_4_days: r.data.push_title_4_days || '',
          push_title_3_days: r.data.push_title_3_days || '',
          push_title_2_days: r.data.push_title_2_days || '',
          push_title_1_day: r.data.push_title_1_day || '',
          push_title_on_day: r.data.push_title_on_day || '',
          push_title_1_day_after: r.data.push_title_1_day_after || '',
          push_body_template: r.data.push_body_template || '',
        });
        // Referral
        setReferralConfig({
          referral_enabled: r.data.referral_enabled || false,
          referral_count_required: r.data.referral_count_required || 1,
          referral_bonus_type: r.data.referral_bonus_type || 'free_month',
          referral_discount_value: r.data.referral_discount_value || 0,
          referral_free_months: r.data.referral_free_months || 1,
          referral_sigma_plan_id: r.data.referral_sigma_plan_id || '',
          referral_limit_per_month: r.data.referral_limit_per_month || 0,
          referral_apply_mode: r.data.referral_apply_mode || 'first_only',
          referral_progress_message: r.data.referral_progress_message || '',
          referral_success_message: r.data.referral_success_message || '',
        });
      }
    } catch {}
  };
  const [pushConfig, setPushConfig] = useState({});
  const [savingPush, setSavingPush] = useState(false);
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [bdayModalOpen, setBdayModalOpen] = useState(false);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [referralConfig, setReferralConfig] = useState({
    referral_enabled: false, referral_count_required: 1, referral_bonus_type: 'free_month',
    referral_discount_value: 0, referral_free_months: 1, referral_sigma_plan_id: '',
    referral_limit_per_month: 0, referral_apply_mode: 'first_only',
    referral_progress_message: '', referral_success_message: ''
  });
  const [savingReferral, setSavingReferral] = useState(false);
  const saveReferralConfig = async () => {
    setSavingReferral(true);
    try {
      await api.put('/customer-settings', referralConfig);
      toast.success('Configuracoes de indicacao salvas!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSavingReferral(false); }
  };
  const savePushConfig = async () => {
    setSavingPush(true);
    try {
      await api.put('/customer-settings', pushConfig);
      toast.success('Configuracoes de push salvas!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSavingPush(false); }
  };
  const saveBirthdaySettings = async () => {
    setSavingBday(true);
    try {
      await api.put('/customer-settings', {
        birthday_gift_enabled: bdayEnabled,
        birthday_gift_type: bdayType,
        birthday_coupon_discount: bdayCouponDiscount,
        birthday_renewal_months: bdayRenewalMonths,
        birthday_sigma_plan_id: bdaySigmaPlanId || null
      });
      toast.success('Configurações de aniversário salvas!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSavingBday(false); }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim() || !couponForm.discount_percent) { toast.error('Preencha código e desconto'); return; }
    setSavingCoupon(true);
    try {
      await api.post('/coupons', { code: couponForm.code.trim(), discount_percent: parseFloat(couponForm.discount_percent), target_type: couponForm.target_type, target_ids: couponForm.target_ids, max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null });
      toast.success('Cupom criado!');
      setCouponForm({ code: '', discount_percent: '', target_type: 'all', target_ids: [], max_uses: '' });
      loadCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSavingCoupon(false); }
  };
  const deleteCoupon = async (id) => { try { await api.delete(`/coupons/${id}`); setCoupons(prev => prev.filter(c => c.id !== id)); toast.success('Cupom removido'); } catch {} };
  const toggleCoupon = async (id) => { try { const r = await api.put(`/coupons/${id}/toggle`); setCoupons(prev => prev.map(c => c.id === id ? { ...c, active: r.data.active } : c)); } catch {} };

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/auth/profile', { support_whatsapp: supportWhatsapp.trim() }); await checkAuth(); toast.success('Salvo!'); }
    catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const saveNotice = async () => {
    setSavingNotice(true);
    try { await api.put('/app-notice', { message: notice }); toast.success(notice.trim() ? 'Aviso atualizado!' : 'Aviso removido!'); }
    catch { toast.error('Erro ao salvar aviso'); }
    finally { setSavingNotice(false); }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande (max 5MB)'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await api.post('/app-banners/upload', { image: reader.result });
          setBanners(prev => [...prev, res.data]);
          toast.success('Banner adicionado!');
        } catch (err) { toast.error(err.response?.data?.detail || 'Erro no upload'); }
        finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); toast.error('Erro ao ler arquivo'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteBanner = async (id) => {
    try { await api.delete(`/app-banners/${id}`); setBanners(prev => prev.filter(b => b.id !== id)); toast.success('Banner removido!'); }
    catch { toast.error('Erro ao remover'); }
  };

  const sendNotification = async () => {
    if (!notifForm.title.trim() || !notifForm.message.trim()) { toast.error('Preencha título e mensagem'); return; }
    if (sendJob?.status === 'running') { toast.error('Aguarde o envio atual finalizar'); return; }
    setSending(true);
    try {
      const payload = { title: notifForm.title.trim(), message: notifForm.message.trim() };
      if (selectedPlanIds.length > 0) payload.plan_ids = selectedPlanIds;
      if (notifForm.button1_text.trim() && notifForm.button1_url.trim()) { payload.button1_text = notifForm.button1_text.trim(); payload.button1_url = notifForm.button1_url.trim(); }
      if (notifForm.button2_text.trim() && notifForm.button2_url.trim()) { payload.button2_text = notifForm.button2_text.trim(); payload.button2_url = notifForm.button2_url.trim(); }
      if (notifForm.expires_in_hours && parseInt(notifForm.expires_in_hours) > 0) { payload.expires_in_hours = parseInt(notifForm.expires_in_hours); }
      const res = await api.post('/custom-notifications/send', payload);
      toast.success(`Envio iniciado para ${res.data.total} clientes!`);
      setSendJob({ id: res.data.job_id, total: res.data.total, sent: 0, pushed: 0, status: 'running' });
      startJobPolling(res.data.job_id);
      setNotifForm({ title: '', message: '', button1_text: '', button1_url: '', button2_text: '', button2_url: '', expires_in_hours: '' });
      setSelectedPlanIds([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSending(false); }
  };

  const fmt = (iso) => { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className="space-y-6" data-testid="app-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">App | Config</h1>
          <p className="text-muted-foreground text-sm mt-1">Configure o app mobile e envie notificações personalizadas</p>
        </div>
        <button onClick={() => { setShowAppUsers(true); fetchAppUsers(); }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
          data-testid="app-users-btn">
          <Download className="w-3.5 h-3.5" /><span>Clientes com App</span>
        </button>
      </div>

      {user?.role === 'simple' && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><Smartphone className="w-5 h-5 text-primary" /></div>
              <div><p className="font-semibold text-sm">Plano Nexu Simple</p><p className="text-xs text-muted-foreground">Seu plano utiliza o App NexuZap Cliente como ferramenta principal.</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ROW 1: Links + Suporte + Aviso (side by side on desktop) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Links */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Smartphone className="w-4 h-4 text-emerald-500" />Acesso dos Clientes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border/50">
              <Smartphone className="w-4 h-4 text-emerald-500 shrink-0" />
              <span className="text-xs truncate flex-1">play.google.com/...nexuzap.app</span>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText('https://play.google.com/store/apps/details?id=com.nexuzap.app'); toast.success('Copiado!'); }}>Copiar</Button>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border/50">
              <ExternalLink className="w-4 h-4 text-blue-500 shrink-0" />
              <span className="text-xs truncate flex-1">nexuzap.com/cliente</span>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText('https://nexuzap.com/cliente'); toast.success('Copiado!'); }}>Copiar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Suporte */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><MessageSquare className="w-4 h-4 text-primary" />Suporte WhatsApp</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input placeholder="5521999999999" value={supportWhatsapp} onChange={(e) => setSupportWhatsapp(e.target.value)} className="flex-1 h-9 text-sm" data-testid="support-whatsapp-input" />
              <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-app-config-btn"><Save className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>

        {/* Aviso Fixo */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-yellow-500" />Aviso Fixo no App</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Textarea placeholder="Ex: Manutenção dia 10/07 das 02h às 06h" value={notice} onChange={(e) => setNotice(e.target.value.slice(0, 200))} rows={2} className="text-sm resize-none" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">{notice.length}/200</span>
                <Button onClick={saveNotice} disabled={savingNotice} size="sm" variant={notice.trim() ? 'default' : 'destructive'}>
                  {notice.trim() ? (savingNotice ? '...' : 'Salvar Aviso') : (savingNotice ? '...' : 'Remover')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 2: Banners */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm"><Image className="w-4 h-4 text-purple-500" />Banners do App <span className="text-xs text-muted-foreground font-normal ml-1">({banners.length}/3)</span></CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap">
            {banners.map(b => (
              <div key={b.id} className="relative group rounded-lg overflow-hidden border border-border/50 w-[200px] h-[100px]">
                <img src={b.url || b.full_url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => deleteBanner(b.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {banners.length < 3 && (
              <label className="w-[200px] h-[100px] rounded-lg border-2 border-dashed border-border/50 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                <Image className="w-6 h-6 text-muted-foreground mb-1" />
                <span className="text-xs text-muted-foreground">{uploading ? 'Enviando...' : 'Adicionar'}</span>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadBanner} disabled={uploading} />
              </label>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Banners aparecem na dashboard do app. Tamanho ideal: 800x400px (paisagem).</p>
        </CardContent>
      </Card>

      {/* ROW 3: Cupons */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm"><CreditCard className="w-4 h-4 text-orange-500" />Cupons de Desconto <span className="text-xs text-muted-foreground font-normal ml-1">({coupons.length})</span></CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Criar */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Código *</Label><Input placeholder="EX: PROMO20" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} className="h-9 text-sm font-mono" /></div>
                <div><Label className="text-xs">Desconto % *</Label><Input type="number" placeholder="20" min="1" max="100" value={couponForm.discount_percent} onChange={(e) => setCouponForm({ ...couponForm, discount_percent: e.target.value })} className="h-9 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Válido para</Label>
                  <select value={couponForm.target_type} onChange={(e) => setCouponForm({ ...couponForm, target_type: e.target.value, target_ids: [] })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="all">Todos</option><option value="plans">Planos específicos</option><option value="customers">Clientes específicos</option>
                  </select>
                </div>
                <div><Label className="text-xs">Limite de usos</Label><Input type="number" placeholder="Ilimitado" value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })} className="h-9 text-sm" /></div>
              </div>
              {/* Botão para selecionar alvos */}
              {couponForm.target_type !== 'all' && (
                <button onClick={() => { setTempTargetIds([...couponForm.target_ids]); setTargetSearch(''); setShowTargetPicker(true); }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-dashed border-border hover:border-primary/40 transition-colors text-xs">
                  <span className="text-muted-foreground">
                    {couponForm.target_ids.length > 0
                      ? `${couponForm.target_ids.length} ${couponForm.target_type === 'plans' ? 'plano(s)' : 'cliente(s)'} selecionado(s)`
                      : `Selecionar ${couponForm.target_type === 'plans' ? 'planos' : 'clientes'}...`
                    }
                  </span>
                  <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
              <Button onClick={createCoupon} disabled={savingCoupon} size="sm" className="w-full bg-orange-600 hover:bg-orange-700 h-8 text-xs">{savingCoupon ? '...' : 'Criar Cupom'}</Button>
            </div>
            {/* Lista */}
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {coupons.length === 0 ? <p className="text-xs text-muted-foreground text-center py-6">Nenhum cupom</p>
               : coupons.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleCoupon(c.id)} className={`w-2 h-2 rounded-full ${c.active ? 'bg-green-500' : 'bg-red-500'}`} title={c.active ? 'Ativo' : 'Inativo'} />
                    <span className="text-sm font-mono font-bold">{c.code}</span>
                    <span className="text-xs text-orange-500 font-semibold">-{c.discount_percent}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">{c.uses} uso(s)</span>
                    <button onClick={() => deleteCoupon(c.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROW 4: Push Notification - Botão + Histórico */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Bell className="w-4 h-4 text-purple-500" />Notificações Push</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Envie notificações push diretamente para o app dos seus clientes. Ideal para avisos de promoções, manutenções ou comunicados importantes. A notificação aparece no celular do cliente mesmo com o app fechado.
            </p>
            <Button onClick={() => setNotifModalOpen(true)} disabled={sendJob?.status === 'running'} className="w-full bg-purple-600 hover:bg-purple-700 h-10">
              {sendJob?.status === 'running' ? (
                <><div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />Enviando...</>
              ) : (
                <><Bell className="w-4 h-4 mr-2" />Enviar Notificacao Push</>
              )}
            </Button>
            {sendJob?.status === 'running' && (
              <div className="space-y-1.5 mt-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-purple-400 font-medium">Progresso do envio</span>
                  <span className="text-muted-foreground font-mono text-[10px]">{sendJob.sent || 0} de {sendJob.total}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-2 bg-purple-500 rounded-full transition-all duration-700 ease-out" style={{ width: `${sendJob.total ? Math.round((sendJob.sent || 0) / sendJob.total * 100) : 0}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{sendJob.pushed || 0} push entregues</span>
                  <span>{sendJob.total ? Math.round((sendJob.sent || 0) / sendJob.total * 100) : 0}%</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Histórico */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Bell className="w-4 h-4 text-muted-foreground" />Histórico</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingNotifs ? <p className="text-sm text-muted-foreground text-center py-4">Carregando...</p>
             : notifications.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Nenhuma notificação</p>
             : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {notifications.map((n) => (
                  <div key={n.id} className="border border-border/50 rounded-lg p-2.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-xs">{n.title}</p>
                      <span className="text-[10px] text-muted-foreground">{fmt(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{n.message}</p>
                    <div className="flex gap-2 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{n.sent_count}</span>
                      <span className="flex items-center gap-0.5"><Bell className="w-2.5 h-2.5" />{n.delivered_count} push</span>
                      {n.plan_ids?.length > 0 && <span className="flex items-center gap-0.5"><Filter className="w-2.5 h-2.5" />{n.plan_ids.length} plano(s)</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ROW 5: Push de Vencimento + Presente de Aniversário + Indicação */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Push de Vencimento */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Push de Vencimento</p>
                <p className="text-xs text-muted-foreground">Personalize os alertas de vencimento no app</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setPushModalOpen(true)}>Configurar</Button>
          </CardContent>
        </Card>

        {/* Presente de Aniversário */}
        <Card data-testid="birthday-gift-config">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/15 flex items-center justify-center shrink-0">
                <Cake className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Presente de Aniversario</p>
                <p className="text-xs text-muted-foreground">{bdayEnabled ? 'Ativo' : 'Desativado'} — {bdayType === 'coupon' ? 'Cupom de desconto' : 'Renovacao automatica'}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setBdayModalOpen(true)}>Configurar</Button>
          </CardContent>
        </Card>

        {/* Sistema de Indicação */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Sistema de Indicacao</p>
                <p className="text-xs text-muted-foreground">{referralConfig.referral_enabled ? 'Ativo' : 'Desativado'} — {referralConfig.referral_bonus_type === 'free_month' ? 'Mes gratis' : 'Desconto'}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setReferralModalOpen(true)}>Configurar</Button>
          </CardContent>
        </Card>
      </div>

      {/* Modal: Push de Vencimento */}
      <Dialog open={pushModalOpen} onOpenChange={setPushModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-orange-500" />Push de Vencimento no App</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground">Quais dias enviar:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: '7_days', label: '7 dias antes' },
                  { key: '5_days', label: '5 dias antes' },
                  { key: '4_days', label: '4 dias antes' },
                  { key: '3_days', label: '3 dias antes' },
                  { key: '2_days', label: '2 dias antes' },
                  { key: '1_day', label: '1 dia antes' },
                  { key: 'on_day', label: 'No dia' },
                  { key: '1_day_after', label: '1 dia depois' },
                ].map(d => (
                  <div key={d.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                    <span className="text-xs">{d.label}</span>
                    <Switch
                      checked={pushConfig[`push_enabled_${d.key}`] !== false}
                      onCheckedChange={(v) => setPushConfig({ ...pushConfig, [`push_enabled_${d.key}`]: v })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground">Titulos personalizados (vazio = padrao):</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { key: '7_days', ph: 'Fatura em 7 dias' },
                  { key: '5_days', ph: 'Fatura em 5 dias' },
                  { key: '3_days', ph: 'Fatura em 3 dias' },
                  { key: '1_day', ph: 'Fatura vence amanha!' },
                  { key: 'on_day', ph: 'Fatura vence HOJE!' },
                  { key: '1_day_after', ph: 'Fatura VENCIDA!' },
                ].map(d => (
                  <Input
                    key={d.key}
                    value={pushConfig[`push_title_${d.key}`] || ''}
                    onChange={(e) => setPushConfig({ ...pushConfig, [`push_title_${d.key}`]: e.target.value })}
                    placeholder={d.ph}
                    className="h-8 text-xs"
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Corpo da notificacao</Label>
              <VarChips vars={['{plano}', '{valor}', '{nome}', '{dias}']} />
              <Input
                value={pushConfig.push_body_template || ''}
                onChange={(e) => setPushConfig({ ...pushConfig, push_body_template: e.target.value })}
                placeholder="Plano: {plano} - {valor}"
                className="h-9 text-xs"
              />
            </div>
            <Button onClick={() => { savePushConfig(); setPushModalOpen(false); }} disabled={savingPush} className="w-full">
              {savingPush ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Presente de Aniversário */}
      <Dialog open={bdayModalOpen} onOpenChange={setBdayModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cake className="w-5 h-5 text-pink-500" />Presente de Aniversario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div>
                <p className="text-sm font-medium">Habilitar</p>
                <p className="text-xs text-muted-foreground">Clientes recebem presente no mes do aniversario</p>
              </div>
              <Switch checked={bdayEnabled} onCheckedChange={setBdayEnabled} />
            </div>
            {bdayEnabled && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Tipo de Presente</Label>
                  <select value={bdayType} onChange={(e) => setBdayType(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1">
                    <option value="coupon">Cupom de Desconto</option>
                    <option value="renewal">Renovacao Automatica</option>
                  </select>
                </div>
                {bdayType === 'coupon' && (
                  <div>
                    <Label className="text-xs">Desconto do Cupom (%)</Label>
                    <Input type="number" min="1" max="100" value={bdayCouponDiscount} onChange={(e) => setBdayCouponDiscount(parseInt(e.target.value) || 0)} className="h-9 text-sm mt-1" />
                  </div>
                )}
                {bdayType === 'renewal' && (
                  <>
                    <div>
                      <Label className="text-xs">Meses de Renovacao</Label>
                      <Input type="number" min="1" max="12" value={bdayRenewalMonths} onChange={(e) => setBdayRenewalMonths(parseInt(e.target.value) || 1)} className="h-9 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Plano Sigma (opcional)</Label>
                      <select value={bdaySigmaPlanId} onChange={(e) => setBdaySigmaPlanId(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1">
                        <option value="">Manter plano atual</option>
                        {plans.filter(p => p.sigma_package_id).map(p => <option key={p.id} value={p.id}>{p.name} - R$ {p.price?.toFixed(2)}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}
            <Button onClick={() => { saveBirthdaySettings(); setBdayModalOpen(false); }} disabled={savingBday} className="w-full">
              {savingBday ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Sistema de Indicação */}
      <Dialog open={referralModalOpen} onOpenChange={setReferralModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" />Sistema de Indicacao</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div>
                <p className="text-sm font-medium">Habilitar Indicacoes</p>
                <p className="text-xs text-muted-foreground">Clientes podem indicar amigos e ganhar bonus</p>
              </div>
              <Switch checked={referralConfig.referral_enabled} onCheckedChange={v => setReferralConfig({...referralConfig, referral_enabled: v})} />
            </div>
            {referralConfig.referral_enabled && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Tipo de Bonus</Label>
                  <select value={referralConfig.referral_bonus_type} onChange={e => setReferralConfig({...referralConfig, referral_bonus_type: e.target.value})} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1">
                    <option value="free_month">Mes(es) Gratis</option>
                    <option value="discount_fixed">Desconto Fixo (R$)</option>
                    <option value="discount_percent">Desconto Percentual (%)</option>
                  </select>
                </div>

                {referralConfig.referral_bonus_type === 'free_month' && (
                  <>
                    <div>
                      <Label className="text-xs">Meses Gratis</Label>
                      <Input type="number" min="1" max="12" value={referralConfig.referral_free_months} onChange={e => setReferralConfig({...referralConfig, referral_free_months: parseInt(e.target.value) || 1})} className="h-9 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Plano Sigma (opcional, pra renovar no Sigma)</Label>
                      <select value={referralConfig.referral_sigma_plan_id} onChange={e => setReferralConfig({...referralConfig, referral_sigma_plan_id: e.target.value})} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1">
                        <option value="">Manter plano atual do cliente</option>
                        {plans.filter(p => p.sigma_package_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  </>
                )}

                {(referralConfig.referral_bonus_type === 'discount_fixed' || referralConfig.referral_bonus_type === 'discount_percent') && (
                  <div>
                    <Label className="text-xs">{referralConfig.referral_bonus_type === 'discount_fixed' ? 'Valor do Desconto (R$)' : 'Percentual de Desconto (%)'}</Label>
                    <Input type="number" min="1" step={referralConfig.referral_bonus_type === 'discount_fixed' ? '0.01' : '1'} value={referralConfig.referral_discount_value} onChange={e => setReferralConfig({...referralConfig, referral_discount_value: parseFloat(e.target.value) || 0})} className="h-9 text-sm mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">Aplicado na renovacao do indicador quando atingir as indicacoes</p>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Indicacoes Necessarias</Label>
                  <Input type="number" min="1" max="50" value={referralConfig.referral_count_required} onChange={e => setReferralConfig({...referralConfig, referral_count_required: parseInt(e.target.value) || 1})} className="h-9 text-sm mt-1" />
                </div>

                <div>
                  <Label className="text-xs">Limite por Mes (0 = ilimitado)</Label>
                  <Input type="number" min="0" value={referralConfig.referral_limit_per_month} onChange={e => setReferralConfig({...referralConfig, referral_limit_per_month: parseInt(e.target.value) || 0})} className="h-9 text-sm mt-1" />
                </div>

                <div>
                  <Label className="text-xs">Aplicacao do Bonus</Label>
                  <select value={referralConfig.referral_apply_mode} onChange={e => setReferralConfig({...referralConfig, referral_apply_mode: e.target.value})} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1">
                    <option value="first_only">Apenas na primeira renovacao (apos indicacao)</option>
                    <option value="recurring">Recorrente (toda renovacao enquanto indicado)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs">Mensagem de Progresso (Push no App)</Label>
                  <VarChips vars={['{indicacoes_feitas}', '{indicacoes_total}', '{indicacoes_faltando}']} />
                  <Input value={referralConfig.referral_progress_message} onChange={e => setReferralConfig({...referralConfig, referral_progress_message: e.target.value})} placeholder="Voce tem {indicacoes_feitas}/{indicacoes_total}! Falta {indicacoes_faltando}" className="h-9 text-xs" />
                </div>

                <div>
                  <Label className="text-xs">Mensagem de Sucesso (Push no App)</Label>
                  <VarChips vars={['{premio}']} />
                  <Input value={referralConfig.referral_success_message} onChange={e => setReferralConfig({...referralConfig, referral_success_message: e.target.value})} placeholder="Parabens! Voce ganhou {premio}!" className="h-9 text-xs" />
                </div>
              </div>
            )}
            <Button onClick={() => { saveReferralConfig(); setReferralModalOpen(false); }} disabled={savingReferral} className="w-full">
              {savingReferral ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Enviar Notificação Push */}
      <Dialog open={notifModalOpen} onOpenChange={(open) => { if (!open) { setNotifForm({ title: '', message: '', button1_text: '', button1_url: '', button2_text: '', button2_url: '', expires_in_hours: '' }); setSelectedPlanIds([]); setButtonCount(0); } setNotifModalOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-purple-500" />Enviar Notificação Push</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-medium">Título *</Label>
              <Input placeholder="Ex: Promoção Especial!" value={notifForm.title} onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })} className="h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium">Mensagem *</Label>
              <Textarea placeholder="Ex: Aproveite 50% de desconto na renovação do seu plano..." value={notifForm.message} onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })} rows={3} className="mt-1 resize-none" />
            </div>

            {/* Botões opcionais */}
            {buttonCount >= 1 && (
              <div className="space-y-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Botão 1</Label>
                  <button onClick={() => { setButtonCount(c => c - 1 < 0 ? 0 : (buttonCount === 1 ? 0 : 1)); setNotifForm(f => ({ ...f, button1_text: buttonCount === 1 ? '' : f.button1_text, button1_url: buttonCount === 1 ? '' : f.button1_url, button2_text: '', button2_url: '' })); }} className="text-xs text-red-400 hover:text-red-300">Remover</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Texto do botão" value={notifForm.button1_text} onChange={(e) => setNotifForm({ ...notifForm, button1_text: e.target.value })} className="h-8 text-xs" />
                  <Input placeholder="https://link.com" value={notifForm.button1_url} onChange={(e) => setNotifForm({ ...notifForm, button1_url: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>
            )}
            {buttonCount >= 2 && (
              <div className="space-y-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Botão 2</Label>
                  <button onClick={() => { setButtonCount(1); setNotifForm(f => ({ ...f, button2_text: '', button2_url: '' })); }} className="text-xs text-red-400 hover:text-red-300">Remover</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Texto do botão" value={notifForm.button2_text} onChange={(e) => setNotifForm({ ...notifForm, button2_text: e.target.value })} className="h-8 text-xs" />
                  <Input placeholder="https://link.com" value={notifForm.button2_url} onChange={(e) => setNotifForm({ ...notifForm, button2_url: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>
            )}
            {buttonCount < 2 && (
              <button onClick={() => setButtonCount(c => c + 1)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-border hover:border-primary/40 text-xs text-muted-foreground hover:text-foreground transition-colors">
                + Adicionar Botão ({buttonCount}/2)
              </button>
            )}

            {/* Prazo de expiração */}
            <div>
              <Label className="text-xs font-medium">Prazo para expirar (horas)</Label>
              <select
                value={notifForm.expires_in_hours}
                onChange={(e) => setNotifForm({ ...notifForm, expires_in_hours: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm mt-1"
              >
                <option value="">Sem prazo (permanente)</option>
                <option value="1">1 hora</option>
                <option value="2">2 horas</option>
                <option value="6">6 horas</option>
                <option value="12">12 horas</option>
                <option value="24">24 horas (1 dia)</option>
                <option value="48">48 horas (2 dias)</option>
                <option value="72">72 horas (3 dias)</option>
                <option value="168">7 dias</option>
                <option value="720">30 dias</option>
              </select>
            </div>

            {/* Filtro de planos */}
            {plans.length > 0 && (
              <div className="border border-border/50 rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Filter className="w-3 h-3" />Filtrar por Plano <span className="text-[10px] opacity-60">(vazio = todos)</span></p>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {plans.map((p) => (
                    <label key={p.id} className="flex items-center gap-1.5 cursor-pointer text-xs bg-muted/30 rounded-md px-2 py-1 hover:bg-muted/50">
                      <Checkbox checked={selectedPlanIds.includes(p.id)} onCheckedChange={(c) => c ? setSelectedPlanIds([...selectedPlanIds, p.id]) : setSelectedPlanIds(selectedPlanIds.filter(id => id !== p.id))} />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Progress bar */}
            {sendJob?.status === 'running' && (
              <div className="space-y-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-primary font-medium">Enviando notificacoes...</span>
                  <span className="text-muted-foreground font-mono">{sendJob.sent || 0}/{sendJob.total}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-2 bg-primary rounded-full transition-all duration-500 ease-out" style={{ width: `${sendJob.total ? Math.round((sendJob.sent || 0) / sendJob.total * 100) : 0}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground">{sendJob.pushed || 0} push enviados</p>
              </div>
            )}

            <Button onClick={() => { sendNotification(); setNotifModalOpen(false); }} disabled={sending || sendJob?.status === 'running' || !notifForm.title.trim() || !notifForm.message.trim()} className="w-full bg-purple-600 hover:bg-purple-700 h-10">
              {sendJob?.status === 'running' ? (
                <><div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />Enviando {sendJob.sent}/{sendJob.total}...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />{sending ? 'Iniciando...' : selectedPlanIds.length > 0 ? `Enviar para ${selectedPlanIds.length} plano(s)` : 'Enviar para Todos'}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Seletor de Planos/Clientes para Cupom */}
      <Dialog open={showTargetPicker} onOpenChange={setShowTargetPicker}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-base">
              {couponForm.target_type === 'plans' ? 'Selecionar Planos' : 'Selecionar Clientes'}
            </DialogTitle>
          </DialogHeader>
          <Input placeholder="Pesquisar por nome ou número..." value={targetSearch} onChange={(e) => setTargetSearch(e.target.value)} className="h-9 text-sm" autoFocus />
          <p className="text-xs text-muted-foreground">{tempTargetIds.length} selecionado(s)</p>
          <div className="overflow-y-auto flex-1 max-h-[350px] space-y-1 -mx-2 px-2">
            {couponForm.target_type === 'plans' && plans
              .filter(p => !targetSearch || p.name?.toLowerCase().includes(targetSearch.toLowerCase()))
              .map(p => (
                <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${tempTargetIds.includes(p.id) ? 'bg-primary/10 border border-primary/20' : 'bg-muted/20 hover:bg-muted/40 border border-transparent'}`}>
                  <Checkbox checked={tempTargetIds.includes(p.id)} onCheckedChange={(c) => setTempTargetIds(prev => c ? [...prev, p.id] : prev.filter(x => x !== p.id))} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">R$ {(p.price || 0).toFixed(2)} — {p.duration_months || 1} mês(es)</p>
                  </div>
                </label>
              ))
            }
            {couponForm.target_type === 'customers' && customers
              .filter(c => {
                if (!targetSearch) return true;
                const q = targetSearch.toLowerCase();
                return (c.name?.toLowerCase().includes(q)) || (c.phone?.includes(targetSearch));
              })
              .map(c => (
                <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${tempTargetIds.includes(c.id) ? 'bg-primary/10 border border-primary/20' : 'bg-muted/20 hover:bg-muted/40 border border-transparent'}`}>
                  <Checkbox checked={tempTargetIds.includes(c.id)} onCheckedChange={(ch) => setTempTargetIds(prev => ch ? [...prev, c.id] : prev.filter(x => x !== c.id))} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.name || 'Sem nome'}</p>
                    <p className="text-xs text-muted-foreground">{c.phone || '—'}</p>
                  </div>
                </label>
              ))
            }
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowTargetPicker(false)}>Cancelar</Button>
            <Button size="sm" className="flex-1" onClick={() => { setCouponForm(prev => ({ ...prev, target_ids: [...tempTargetIds] })); setShowTargetPicker(false); }}>
              Salvar ({tempTargetIds.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Clientes com App */}
      <Dialog open={showAppUsers} onOpenChange={setShowAppUsers}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Download className="w-4 h-4 text-primary" />Clientes com App Instalado</DialogTitle></DialogHeader>
          {loadingAppUsers ? <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
           : appUsers.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Nenhum cliente baixou o app ainda.</p>
           : (<>
              <p className="text-xs text-muted-foreground">{appUsers.length} cliente(s)</p>
              <div className="overflow-y-auto max-h-[280px] -mx-2 px-2 space-y-1.5">
                {appUsers.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{c.name || 'Sem nome'}</p><p className="text-xs text-muted-foreground">{c.phone || '—'}</p></div>
                    <div className="text-right shrink-0 ml-3">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">{c.platform}</span>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{c.devices} disp.</p>
                    </div>
                  </div>
                ))}
              </div>
            </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
